import { useState } from 'react';
import { formatearCLP, type RemuneracionItem } from '@lexicore/shared';
import { useAvisos } from '../../contextos/AvisosContext';
import { IconoCerrar } from '../../componentes/Iconos';
import { useMarcarRemuneracionPagada } from '../../lib/mutaciones';

interface ModalPagarRemuneracionProps {
  remuneracion: RemuneracionItem | null;
  alCerrar: () => void;
}

export function ModalPagarRemuneracion({
  remuneracion,
  alCerrar,
}: ModalPagarRemuneracionProps) {
  const avisos = useAvisos();
  const pagarMut = useMarcarRemuneracionPagada();

  const [fechaPago, setFechaPago] = useState(() => new Date().toISOString().slice(0, 10));
  const [medioPago, setMedioPago] = useState('TRANSFERENCIA');
  const [referencia, setReferencia] = useState('');

  if (!remuneracion) return null;

  const totalAPagar = remuneracion.sueldoLiquido + (remuneracion.bonos || 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await pagarMut.mutateAsync({
        id: remuneracion.id,
        datos: {
          fechaPago: new Date(fechaPago).toISOString(),
          referencia: referencia.trim()
            ? `[${medioPago}] ${referencia.trim()}`
            : `Pago de sueldo procesado vía ${medioPago}`,
        },
      });

      avisos.exito(`Sueldo de ${remuneracion.nombreEmpleado} marcado como pagado exitosamente.`);
      alCerrar();
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'Error al registrar el pago de sueldo.');
    }
  };

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div
        className="modal"
        style={{ maxWidth: 480, width: '100%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-color)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>💳</span>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
              Registrar Pago de Sueldo
            </h3>
          </div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={alCerrar}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              padding: 4,
            }}
            aria-label="Cerrar modal"
          >
            <IconoCerrar size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Tarjeta de Resumen del Colaborador */}
          <div
            style={{
              padding: '14px 16px',
              background: 'var(--bg-muted, rgba(255,255,255,0.03))',
              borderRadius: 6,
              border: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                {remuneracion.nombreEmpleado}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {remuneracion.cargo} · {remuneracion.prevision}
              </div>
              {remuneracion.rutEmpleado && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  RUT: {remuneracion.rutEmpleado}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Total a Transferir</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent-green, #10b981)' }}>
                {formatearCLP(totalAPagar)}
              </div>
              {remuneracion.bonos > 0 && (
                <div style={{ fontSize: 10.5, color: 'var(--accent-purple, #8b5cf6)' }}>
                  Incluye +{formatearCLP(remuneracion.bonos)} de bono
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label htmlFor="pago_fecha">Fecha Efectiva de Pago *</label>
              <input
                id="pago_fecha"
                type="date"
                required
                value={fechaPago}
                onChange={(e) => setFechaPago(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="pago_medio">Medio de Pago</label>
              <select
                id="pago_medio"
                value={medioPago}
                onChange={(e) => setMedioPago(e.target.value)}
              >
                <option value="TRANSFERENCIA">Transferencia Bancaria</option>
                <option value="VALE_VISTA">Vale Vista Bancario</option>
                <option value="CHEQUE">Cheque Nominativo</option>
                <option value="EFECTIVO">Efectivo / Caja Chica</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="pago_ref">Referencia o N° de Comprobante</label>
            <input
              id="pago_ref"
              type="text"
              placeholder="Ej: Lote Nómina Banco Santander #8492"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
            />
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 10,
              paddingTop: 12,
              borderTop: '1px solid var(--border-color)',
            }}
          >
            <button type="button" className="btn btn-secondary" onClick={alCerrar}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={pagarMut.isPending}>
              {pagarMut.isPending ? 'Procesando Pago…' : 'Confirmar Pago de Sueldo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
